import React from "react";
import Container from "src/components/common/Layout/Container";
import { Box, Button, SelectChangeEvent, Typography } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { colors } from "src/config/colors";
import FiltersModal, {
  FILTER_OPTION,
  FilterModalCheckbox,
} from "src/components/ReservationProduct/components/FiltersModal/FiltersModal";
import AddressAutocomplete from "src/components/ReservationProduct/components/AddressAutocomplete";
import { AddressPlacemarkState } from "src/components/ReservationProduct/ReservationProduct";
import {
  EnumMetrikActions,
  sendCustomYandexMetrik,
} from "src/config/analytics/sendCustomYandexMetrik";
import { SearchAddressSchema } from "src/models/findMapAddress/autocompleteSearchAddress";

interface ReservationLayoutProps {
  setOpenMap: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenMap: boolean;
  closeModal: () => void;
  filterOption: FILTER_OPTION;
  setFilterOption: (option: FILTER_OPTION) => void;
  setOpenReservationPanel: React.Dispatch<React.SetStateAction<boolean>>;
  onClickFiltersSubmit: (data: FilterModalCheckbox) => void;
  setFiltersData: (data: FilterModalCheckbox) => void;
  setCoords: (addressCoords: [number, number]) => void;
  setAddressPlacemark: (state: AddressPlacemarkState | null) => void;
  addressList: SearchAddressSchema[];
  setSelectedAddressPharmacy: React.Dispatch<React.SetStateAction<string>>;
}

const ReservationLayout: React.FC<ReservationLayoutProps> = ({
  children,
  setOpenMap,
  isOpenMap,
  closeModal,
  filterOption,
  setFilterOption,
  setOpenReservationPanel,
  onClickFiltersSubmit,
  setFiltersData,
  setCoords,
  setAddressPlacemark,
  addressList,
  setSelectedAddressPharmacy,
}) => {
  const handleVisibleMap = () => {
    if (!isOpenMap) {
      sendCustomYandexMetrik(
        EnumMetrikActions.clickOnShowOffersListBtnOnOffersMap
      );
    }
    setOpenMap((prevState) => !prevState);
    setOpenReservationPanel(false);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    sendCustomYandexMetrik(EnumMetrikActions.clickOnAnySortingOnOffersList);
    setFilterOption(event.target.value as FILTER_OPTION);
  };

  const onClickBack = () => {
    sendCustomYandexMetrik(EnumMetrikActions.clickOnReturnBackBtnOnOffersList);
    closeModal();
    setFiltersData({
      underTheOrder: true,
      available: true,
      aroundTheClock: false,
    });
  };

  return (
    <Container
      wrapperSx={{
        overflow: isOpenMap ? "auto" : "hidden",
        height: "100%",
      }}
      paddingBottom={3.5}
      paddingX={{ xs: 0, md: 2.5, lg: 3.75 }}
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          position: "sticky",
          justifyContent: { xs: "space-between", tb: "start" },
          top: 0,
          backgroundColor: colors.white,
          paddingY: { xs: "10px", md: "15px" },
          paddingX: { xs: "20px", md: "20px" },
          zIndex: 11,
        }}
      >
        <Button
          onClick={onClickBack}
          variant="outlined"
          disableElevation
          sx={{
            display: { xs: "none", md: "flex" },
            maxHeight: "37px",
            textTransform: "none",

            fontWeight: "400",
            alignItems: "center",
          }}
        >
          <KeyboardArrowLeftIcon />
          <Typography
            sx={{
              fontSize: { xs: "11px", md: "14px" },
              lineHeight: { xs: "11px", md: "14px" },
              color: colors.blue,
              textAlign: "center",
            }}
          >
            Назад
          </Typography>
        </Button>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleVisibleMap}
            variant="outlined"
            disableElevation
            sx={{
              display: { xs: "none", md: "flex" },
              backgroundColor: colors.yellow,
              maxHeight: "37px",
              textTransform: "none",
              fontWeight: "400",
              alignItems: "center",
              mr: 3,
              ":hover": {
                backgroundColor: colors.darkYellow,
              },
            }}
          >
            {isOpenMap ? <LocationOnOutlinedIcon /> : <MenuOutlinedIcon />}
            <Typography
              sx={{
                fontSize: { xs: "11px", md: "14px" },
                lineHeight: { xs: "11px", md: "14px" },
                color: colors.blue,
                textAlign: "center",
                ml: "3px",
              }}
            >
              {isOpenMap ? "Смотреть на карте" : "Смотреть списком"}
            </Typography>
          </Button>
          <AddressAutocomplete
            addressList={addressList}
            setCoords={setCoords}
            setAddressPlacemark={setAddressPlacemark}
            isOpenMap={isOpenMap}
            setSelectedAddressPharmacy={setSelectedAddressPharmacy}
          />
          <FiltersModal
            isOpenMap={isOpenMap}
            onClickFiltersSubmit={onClickFiltersSubmit}
            handleSelectChange={handleSelectChange}
            filterOption={filterOption}
            setFilterOption={setFilterOption}
          />
        </Box>
      </Box>
      {children}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          height: "70px",
          width: "100%",
          backgroundColor: colors.white,
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          padding: "15px",
          boxShadow: "0px -1px 4px rgba(0, 0, 0, 0.25)",
        }}
      >
        <Button
          onClick={() => {
            closeModal();
            sendCustomYandexMetrik(
              EnumMetrikActions.clickOnReturnBackBtnOnOffersList
            );
          }}
          variant="outlined"
          disableElevation
          sx={{
            minHeight: "40px",
            width: "167px",
            textTransform: "none",
            fontWeight: "400",
            alignItems: "center",
            mr: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "11px", md: "14px" },
              lineHeight: { xs: "11px", md: "14px" },
              color: colors.blue,
              textAlign: "center",
            }}
          >
            Вернуться
          </Typography>
        </Button>
        <Button
          onClick={handleVisibleMap}
          variant="outlined"
          disableElevation
          sx={{
            minHeight: "40px",
            width: "167px",
            textTransform: "none",
            fontWeight: "400",
            alignItems: "center",
            backgroundColor: colors.yellow,
            ":hover": {
              backgroundColor: colors.darkYellow,
            },
          }}
        >
          {isOpenMap ? <LocationOnOutlinedIcon /> : <MenuOutlinedIcon />}
          <Typography
            sx={{
              fontSize: { xs: "11px", md: "14px" },
              lineHeight: { xs: "11px", md: "14px" },
              color: colors.blue,
              textAlign: "center",
              ml: "3px",
            }}
          >
            {isOpenMap ? "Смотреть на карте" : "Смотреть списком"}
          </Typography>
        </Button>
      </Box>
    </Container>
  );
};

export default ReservationLayout;
